import { type FC, type ReactNode, useRef } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { supabase } from "@/shared/lib/supabase";
import { uploadSupportingDocument } from "@/shared/utils/uploadSupportingDocument";
import { ReviewScreenLayout } from "@/pages/postloss/modules/components/ReviewScreenLayout";
import { LinkedDebtSummary } from "@/pages/postloss/modules/components/LinkedDebtSummary";
import { useReviewRedirect } from "@/pages/postloss/modules/hooks/useReviewRedirect";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { EstateItem } from "@/shared/types/estateTypes";

import {
  PropertyForm,
  buildItemName,
  parseItemNameToForm,
  validatePropertyFields,
  propertyToItemFields,
  type PropertyFormValues,
} from "./PropertyForm";

export const PropertiesReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const {
    assets,
    debts,
    isLoading,
    isFetching,
    updateItem,
    isUpdatingItem,
    deleteItem,
    itemActions,
  } = useEstateItemActions(estateId);
  const dialogRef = useRef<HTMLDivElement>(null);

  const allItems = [...assets, ...debts];
  const items = filterByCategory(assets, "Properties");
  const firstName = person?.first_name ?? "their";
  const hasExtracted = items.some((p) => p.extracted_from_document);

  const { itemsEverFound, alreadyRedirected } = useReviewRedirect({
    items,
    isLoading,
    isFetching,
    answers,
    setAnswer,
    goToScreen,
    reviewKey: "properties_review",
    addKey: "add_property",
    fallbackScreen: "has_property",
    fallbackAnswers: { ...answers, properties_review: "none", add_property: "" },
  });

  const parseItemToForm = (item: EstateItem): PropertyFormValues =>
    parseItemNameToForm(
      item.item_name,
      item.item_type ?? "",
      item.balance,
      item.metadata,
      item.ownership,
    );

  const handleSaveEdit = async (editing: {
    id: number;
    form: PropertyFormValues;
    existingFileName: string | null;
    fileRemoved: boolean;
  }) => {
    const { id, form, fileRemoved } = editing;
    if (!estateId) return;

    const validationError = Object.values(validatePropertyFields(form))[0];
    if (validationError) throw new Error(validationError);

    let sourceDocumentId: number | undefined;

    if (form.deedFile) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;
      sourceDocumentId = await uploadSupportingDocument(
        form.deedFile,
        estateId,
        session.access_token,
        "property_deed",
      );
    }

    const sourceDocUpdate = sourceDocumentId
      ? { source_document_id: sourceDocumentId }
      : fileRemoved
        ? { source_document_id: null }
        : {};

    const fields = propertyToItemFields(form);
    await updateItem(id, {
      item_name: buildItemName(form),
      item_type: fields.item_type,
      balance: fields.balance,
      ownership: fields.ownership,
      metadata: fields.metadata,
      ...sourceDocUpdate,
    });
  };

  return (
    <ReviewScreenLayout<PropertyFormValues>
      items={items}
      isLoading={isLoading}
      isFetching={isFetching}
      isRedirecting={items.length === 0 && !itemsEverFound && !alreadyRedirected}
      screenTitle={
        hasExtracted ? `We found ${firstName}'s properties` : `Review ${firstName}'s properties`
      }
      screenSubtitle={
        hasExtracted
          ? "Take a minute to review this. Edit anything that looks off, and we'll add it to the estate inventory."
          : "Review the details and add any properties we might have missed."
      }
      itemLabel="properties"
      listTitle="Properties"
      renderItem={(item: EstateItem, defaultRow: ReactNode) => (
        <LinkedDebtSummary item={item} allItems={allItems} defaultRow={defaultRow} />
      )}
      editDialogTitle="Edit property"
      editDialogRef={dialogRef}
      deleteDialogTitle="Delete Property"
      parseItemToForm={parseItemToForm}
      renderEditForm={({ form, onChange, existingFileName, onFileRemove }) => (
        <PropertyForm
          form={form}
          onChange={onChange}
          existingDeedFileName={existingFileName}
          onDeedRemoved={onFileRemove}
          portalContainer={dialogRef.current}
        />
      )}
      onSaveEdit={handleSaveEdit}
      isUpdatingItem={isUpdatingItem}
      deleteItem={deleteItem}
      itemActions={itemActions}
      onAddAnother={() => {
        goToScreen("add_property", {
          ...answers,
          has_property: "yes",
          add_property: "adding",
        });
      }}
      onContinue={() => {
        setAnswer("add_property", "done");
        onComplete();
      }}
    />
  );
};
